import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowForward,
  AssessmentOutlined,
  CalendarMonthOutlined,
  CheckCircleOutline,
  InsightsOutlined,
  PlayArrow,
  PsychologyOutlined,
  SpeedOutlined,
  TrendingUp,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser } from "../context/AuthContext";
import { authFetch, getApiErrorMessage, throwForResponse } from "../utils/apiClient";
import { getApiBaseUrl } from "../utils/apiBaseUrl";
import { formatInterviewTypeLabel } from "../utils/interviewTypeLabels";

const API_BASE = getApiBaseUrl();
const borderColor = "#E8ECF4";
const mutedBackground = "#F8FAFC";
const primarySoft = "#F3F5FF";

const panelSx = {
  border: `1px solid ${borderColor}`,
  borderRadius: 2,
  boxShadow: "none",
  backgroundColor: "#FFFFFF",
};

function MetricCard({ icon, label, value, note, color = "primary.main" }) {
  return (
    <Paper sx={{ ...panelSx, p: 2.5, height: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ fontSize: "1.75rem", letterSpacing: 0 }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            color,
            backgroundColor: primarySoft,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        {note}
      </Typography>
    </Paper>
  );
}

function ScoreRow({ label, value, color }) {
  const numericValue = Number(value);
  const hasValue = value != null && Number.isFinite(numericValue);
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{hasValue ? `${Math.round(numericValue)}%` : "--"}</Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={hasValue ? Math.max(0, Math.min(100, numericValue)) : 0}
        aria-label={`${label}: ${hasValue ? `${Math.round(numericValue)} percent` : "not available"}`}
        sx={{
          height: 5,
          borderRadius: 999,
          backgroundColor: "#EEF1F6",
          "& .MuiLinearProgress-bar": { backgroundColor: color, borderRadius: 999 },
        }}
      />
    </Box>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { getToken, isLoaded } = useAuth();
  const [backendUser, setBackendUser] = useState(null);
  const [profileName, setProfileName] = useState("");
  const [syncStatus, setSyncStatus] = useState("idle");
  const [syncError, setSyncError] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyReports, setHistoryReports] = useState([]);
  const [profileCompletionScore, setProfileCompletionScore] = useState(null);

  const syncUser = useCallback(async () => {
    setSyncError(null);
    if (!isLoaded) return;
    const token = await getToken();
    if (!token) {
      setSyncStatus("idle");
      return;
    }
    setSyncStatus("syncing");
    try {
      const [response, profileResponse] = await Promise.all([
        authFetch(`${API_BASE}/api/me`, token, { method: "GET" }),
        authFetch(`${API_BASE}/api/profile/me`, token, { method: "GET" }).catch(() => null),
      ]);
      await throwForResponse(response, { defaultMessage: "Failed to sync user with backend." });
      setBackendUser(await response.json());
      if (profileResponse?.ok) {
        const profilePayload = await profileResponse.json();
        setProfileName(
          String(profilePayload?.fullNameOverride || profilePayload?.candidateProfileV2?.fullNameOverride || "").trim(),
        );
      }
      setSyncStatus("ok");
    } catch (error) {
      setSyncError(getApiErrorMessage(error, { defaultMessage: "Failed to sync user with backend." }));
      setSyncStatus("error");
    }
  }, [getToken, isLoaded]);

  const loadHistory = useCallback(async () => {
    if (!isLoaded) return;
    setHistoryError(null);
    setHistoryLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        setHistoryReports([]);
        return;
      }
      const response = await authFetch(`${API_BASE}/api/interview/reports`, token, { method: "GET" });
      await throwForResponse(response, { defaultMessage: "Failed to fetch interview history." });
      const data = await response.json();
      setHistoryReports(Array.isArray(data) ? data : []);
    } catch (error) {
      setHistoryError(getApiErrorMessage(error, { defaultMessage: "Failed to load interview history." }));
    } finally {
      setHistoryLoading(false);
    }
  }, [getToken, isLoaded]);

  const loadProfileCompletion = useCallback(async () => {
    if (!isLoaded) return;
    try {
      const token = await getToken();
      if (!token) return;
      const response = await authFetch(`${API_BASE}/api/profile`, token, { method: "GET" });
      if (!response.ok) return;
      const data = await response.json();
      const score = Number(data?.profile?.profileCompletionScore);
      setProfileCompletionScore(Number.isFinite(score) ? score : null);
    } catch (_error) {
      // Optional enrichment must not block the dashboard.
    }
  }, [getToken, isLoaded]);

  useEffect(() => {
    if (isLoaded) syncUser();
  }, [isLoaded, syncUser]);

  useEffect(() => {
    if (isLoaded) loadHistory();
  }, [isLoaded, loadHistory]);

  useEffect(() => {
    if (isLoaded) loadProfileCompletion();
  }, [isLoaded, loadProfileCompletion]);

  const summary = useMemo(() => {
    const scores = historyReports.map((report) => Number(report.score)).filter(Number.isFinite);
    const confidenceScores = historyReports.map((report) => Number(report.confidenceScore)).filter(Number.isFinite);
    const improvementScores = historyReports.map((report) => Number(report.improvementDelta)).filter(Number.isFinite);
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const practiceDays = new Set(
      historyReports
        .map((report) => new Date(report.date))
        .filter((date) => !Number.isNaN(date.getTime()) && date.getTime() >= sevenDaysAgo)
        .map((date) => date.toISOString().slice(0, 10)),
    ).size;
    const average = (values) => values.length
      ? Math.round(values.reduce((total, value) => total + value, 0) / values.length)
      : null;

    return {
      total: historyReports.length,
      averageScore: average(scores),
      bestScore: scores.length ? Math.max(...scores) : null,
      averageConfidence: average(confidenceScores),
      averageImprovement: average(improvementScores),
      practiceDays,
      latest: historyReports[0] || null,
    };
  }, [historyReports]);

  const displayName = profileName || user?.name || user?.full_name || backendUser?.full_name || user?.email?.split("@")[0] || "there";
  const firstName = displayName.trim().split(/\s+/)[0] || "there";
  const readinessScore = summary.averageScore;
  const recommendedType = summary.latest?.type ? formatInterviewTypeLabel(summary.latest.type) : "Guided interview";

  return (
    <Box sx={{ minHeight: "100%", backgroundColor: "#FFFFFF", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="xl" sx={{ px: { xs: 2.5, sm: 4, lg: 8 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography component="h1" variant="h3" sx={{ fontSize: { xs: "1.75rem", md: "2rem" }, letterSpacing: 0 }}>
                Welcome back, {firstName}
              </Typography>
              {syncStatus === "syncing" && <CircularProgress size={16} aria-label="Syncing account" />}
              {syncStatus === "ok" && <CheckCircleOutline color="success" sx={{ fontSize: 18 }} aria-label="Account synced" />}
            </Stack>
            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
              Here is your interview readiness at a glance.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            onClick={() => navigate("/interview-config")}
            sx={{ alignSelf: { xs: "stretch", sm: "center" }, borderRadius: 1.5, minWidth: 168 }}
          >
            Start Interview
          </Button>
        </Stack>

        {syncError && (
          <Alert severity="warning" onClose={() => setSyncError(null)} sx={{ mb: 3, borderRadius: 1.5 }}>
            {syncError}
          </Alert>
        )}

        {profileCompletionScore != null && profileCompletionScore < 100 && (
          <Alert
            severity="info"
            action={<Button color="inherit" size="small" onClick={() => navigate("/onboarding")}>Complete profile</Button>}
            sx={{ mb: 3, borderRadius: 1.5 }}
          >
            Add optional profile details over time for more personalized interview feedback.
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
            gap: 2,
            mb: 2,
          }}
        >
          <MetricCard icon={<SpeedOutlined />} label="Readiness score" value={readinessScore == null ? "--" : `${readinessScore}%`} note="Average across saved reports" />
          <MetricCard icon={<CalendarMonthOutlined />} label="Practice this week" value={`${summary.practiceDays} days`} note="Unique practice days in the last week" color="info.main" />
          <MetricCard icon={<AssessmentOutlined />} label="Completed interviews" value={summary.total} note="Sessions saved to your account" color="success.main" />
          <MetricCard icon={<TrendingUp />} label="Best score" value={summary.bestScore == null ? "--" : `${summary.bestScore}%`} note="Your highest recorded result" color="warning.main" />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.35fr) minmax(300px, .65fr)" }, gap: 2, mb: 2 }}>
          <Paper sx={{ ...panelSx, p: { xs: 2.5, md: 3 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems={{ xs: "flex-start", sm: "center" }}>
              <Box sx={{ position: "relative", width: 116, height: 116, flexShrink: 0 }} aria-label={`Readiness score ${readinessScore ?? "not available"}`}>
                <CircularProgress variant="determinate" value={100} size={116} thickness={3.5} sx={{ color: "#EEF1F6", position: "absolute" }} />
                <CircularProgress variant="determinate" value={readinessScore || 0} size={116} thickness={3.5} sx={{ color: "primary.main", position: "absolute" }} />
                <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center" }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontSize: "1.65rem", letterSpacing: 0 }}>{readinessScore ?? "--"}</Typography>
                    <Typography variant="caption" color="text.secondary">Readiness</Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography component="h2" variant="h5" sx={{ mb: 0.75 }}>Your performance overview</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
                  {summary.total
                    ? `Based on ${summary.total} completed ${summary.total === 1 ? "interview" : "interviews"}. Use your latest coaching gap to focus the next session.`
                    : "Complete your first interview to build a readiness score and receive focused coaching."}
                </Typography>
                <Button
                  variant="text"
                  endIcon={<ArrowForward />}
                  onClick={() => summary.latest?.id ? navigate(summary.latest.reportLink || `/report/${summary.latest.id}`) : navigate("/interviews")}
                  sx={{ mt: 1.5, px: 0, minHeight: 36 }}
                >
                  {summary.latest ? "View latest report" : "Start first interview"}
                </Button>
              </Box>
            </Stack>
          </Paper>

          <Paper sx={{ ...panelSx, p: { xs: 2.5, md: 3 }, backgroundColor: primarySoft }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: "grid", placeItems: "center", color: "primary.main", backgroundColor: "#FFFFFF" }}>
                <PsychologyOutlined />
              </Box>
              <Box>
                <Typography component="h2" variant="h6">Recommended next session</Typography>
                <Typography variant="caption" color="text.secondary">Personalized from your latest report</Typography>
              </Box>
            </Stack>
            <Typography sx={{ fontWeight: 700, mb: 0.75 }}>{recommendedType}</Typography>
            <Typography variant="body2" color="text.secondary">
              {summary.latest?.topGap ? `Focus area: ${summary.latest.topGap}` : "Start with a guided session to identify your strongest improvement area."}
            </Typography>
            <Button variant="outlined" endIcon={<ArrowForward />} onClick={() => navigate("/interviews")} sx={{ mt: 2.5, borderRadius: 1.5, backgroundColor: "#FFFFFF" }}>
              Choose a session
            </Button>
          </Paper>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, .7fr) minmax(0, 1.3fr)" }, gap: 2 }}>
          <Paper sx={{ ...panelSx, p: { xs: 2.5, md: 3 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Box>
                <Typography component="h2" variant="h5">Skill performance</Typography>
                <Typography variant="caption" color="text.secondary">Average across available report metrics</Typography>
              </Box>
              <InsightsOutlined color="primary" />
            </Stack>
            <Stack spacing={2.5}>
              <ScoreRow label="Overall score" value={summary.averageScore} color="#4F6BFF" />
              <ScoreRow label="Confidence" value={summary.averageConfidence} color="#21C36F" />
              <ScoreRow label="Session improvement" value={summary.averageImprovement} color="#786BFF" />
            </Stack>
            {summary.latest?.topStrength && (
              <Box sx={{ mt: 3, p: 2, borderRadius: 1.5, backgroundColor: mutedBackground }}>
                <Typography variant="caption" color="text.secondary">Latest strength</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>{summary.latest.topStrength}</Typography>
              </Box>
            )}
          </Paper>

          <Paper sx={{ ...panelSx, overflow: "hidden" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: { xs: 2.5, md: 3 }, py: 2.5, borderBottom: `1px solid ${borderColor}` }}>
              <Box>
                <Typography component="h2" variant="h5">Recent interviews</Typography>
                <Typography variant="caption" color="text.secondary">Your latest saved sessions</Typography>
              </Box>
              {historyReports.length > 0 && (
                <Button variant="text" onClick={() => navigate("/analytics")} sx={{ minHeight: 36 }}>View analytics</Button>
              )}
            </Stack>

            {historyError && (
              <Alert severity="warning" onClose={() => setHistoryError(null)} sx={{ m: 2.5 }}>
                {historyError}
              </Alert>
            )}
            {historyLoading && (
              <Stack spacing={1.5} sx={{ p: 2.5 }} aria-label="Loading interview history">
                {[0, 1, 2].map((item) => <Skeleton key={item} variant="rounded" height={54} />)}
              </Stack>
            )}
            {!historyLoading && !historyError && historyReports.length === 0 && (
              <Stack alignItems="center" textAlign="center" spacing={1.5} sx={{ px: 3, py: 6 }}>
                <AssessmentOutlined sx={{ fontSize: 34, color: "text.disabled" }} />
                <Typography sx={{ fontWeight: 700 }}>No interviews yet</Typography>
                <Typography variant="body2" color="text.secondary">Your session history and reports will appear here.</Typography>
                <Button variant="contained" onClick={() => navigate("/interviews")} sx={{ mt: 1, borderRadius: 1.5 }}>Start Interview</Button>
              </Stack>
            )}
            {!historyLoading && historyReports.length > 0 && (
              <>
                <TableContainer sx={{ display: { xs: "none", sm: "block" } }}>
                  <Table size="small" aria-label="Recent interview sessions">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: mutedBackground }}>
                        <TableCell>Interview</TableCell>
                        <TableCell>Focus</TableCell>
                        <TableCell align="right">Score</TableCell>
                        <TableCell align="right">Report</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historyReports.slice(0, 5).map((report) => (
                        <TableRow key={report.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{report.title || formatInterviewTypeLabel(report.type)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {report.date ? new Date(report.date).toLocaleDateString() : "Date unavailable"}
                            </Typography>
                          </TableCell>
                          <TableCell>{report.track || report.topGap || "--"}</TableCell>
                          <TableCell align="right"><Chip size="small" label={report.score == null ? "--" : `${report.score}%`} sx={{ backgroundColor: primarySoft, color: "primary.dark" }} /></TableCell>
                          <TableCell align="right">
                            <Button size="small" variant="text" onClick={() => navigate(report.reportLink || `/report/${report.id}`)}>View</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Stack sx={{ display: { xs: "flex", sm: "none" }, p: 2 }} divider={<Box sx={{ borderTop: `1px solid ${borderColor}` }} />}>
                  {historyReports.slice(0, 5).map((report) => (
                    <Button
                      key={report.id}
                      variant="text"
                      onClick={() => navigate(report.reportLink || `/report/${report.id}`)}
                      endIcon={<ArrowForward />}
                      sx={{ justifyContent: "space-between", px: 0.5, py: 1.75, borderRadius: 1, color: "text.primary", textAlign: "left" }}
                    >
                      <Box component="span">
                        <Typography component="span" variant="body2" sx={{ display: "block", fontWeight: 700 }}>{report.title || formatInterviewTypeLabel(report.type)}</Typography>
                        <Typography component="span" variant="caption" color="text.secondary">
                          {report.score == null ? "Score unavailable" : `${report.score}% score`}
                        </Typography>
                      </Box>
                    </Button>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}
